import React from "react";
import { FileSearch, CheckCircle2, Loader2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ExtractionStage } from "@shared/list-progress";

/**
 * Loading state shown while a list photo/PDF upload is being read, its items
 * extracted, and its leading results ranked. Distinct from the re-ranking
 * animations (`AiRerankLoading`, `AiRerankLoadingLto`): a checklist of the
 * real stages, checked off as the server reports them.
 *
 * Driven by real signals, not a timer: `stage` is the extraction stage streamed
 * from POST /api/list/photo, and `searching` flips true once extraction is done
 * and the leading items are being searched/ranked (the final step).
 */
const STEPS = [
  "Reading your file",
  "Finding items in your file",
  "Interpreting",
  "Searching for products",
];

/** Index of the checklist step that maps to each extraction stage. */
const STAGE_STEP: Record<ExtractionStage, number> = {
  reading: 0,
  extracting: 1,
  interpreting: 2,
};

const SEARCHING_STEP = 3;

interface ListProcessingLoadingProps {
  className?: string;
  /** Latest extraction stage streamed from the server; null before any arrive. */
  stage?: ExtractionStage | null;
  /** True once extraction is done and the leading items are being searched/ranked. */
  searching?: boolean;
}

export const ListProcessingLoading: React.FC<ListProcessingLoadingProps> = ({
  className,
  stage,
  searching,
}) => {
  const activeStep = searching
    ? SEARCHING_STEP
    : stage
      ? STAGE_STEP[stage]
      : 0;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-5 py-6",
        className,
      )}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-violet-100 dark:from-sky-950/40 dark:to-violet-950/40">
        <FileSearch className="h-7 w-7 text-violet-600 dark:text-violet-400" />
      </div>

      <ul className="w-full max-w-xs space-y-2.5">
        {STEPS.map((label, index) => {
          const isDone = index < activeStep;
          const isActive = index === activeStep;
          return (
            <li key={label} className="flex items-center gap-2.5 text-sm">
              {isDone ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              ) : isActive ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-violet-500" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
              )}
              <span
                className={cn(
                  "transition-colors",
                  isDone || isActive
                    ? "text-foreground"
                    : "text-muted-foreground/40",
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
